import React, { useState } from 'react';
import { Calendar, Clock, ArrowRight, X, Play } from 'lucide-react';
import { Button } from './ui/button';

const blogPosts = [
  {
    id: 1,
    title: "How to Organise a Parking Suspension in Brighton & Hove (Without the Headache)",
    excerpt: "Moving house is stressful enough without worrying about where the van is going to park. If you live in Brighton or Hove, you know that parking is gold dust.",
    image: "https://customer-assets.emergentagent.com/job_98b7cebb-f063-4be0-8c8c-dc4674976523/artifacts/fto3delp_brighton-920314_1280.jpg",
    date: "January 2025",
    readTime: "3 min read",
    type: "article",
    content: {
      intro: "Moving house is stressful enough without worrying about where the van is going to park. If you live in Brighton or Hove, you know that parking is gold dust. The last thing you want on moving day is for our crew to arrive and have to park three streets away because the space outside your house is full.",
      solution: "The solution? A Parking Bay Suspension. Here is the HappyVan guide to navigating the Brighton & Hove Council rules, costs, and timelines.",
      sections: [
        {
          title: "Do I actually need a suspension?",
          content: "If you have a private driveway big enough for a Luton van (approx. 7–8 metres long), then no! However, if you live in a Controlled Parking Zone (CPZ)—which covers most of Brighton, Hove, and even parts of Portslade—you likely need one."
        },
        {
          title: "How much space do I need?",
          content: "A standard parking bay in Brighton is 5.5 metres long. For a Standard Van (Luton), we recommend suspending 2 bays (11 metres). For a Large Move (2 Vans), you will need 3 or 4 bays. Tip: Always overestimate!"
        },
        {
          title: "The Golden Rule: The 7-Day Deadline",
          content: "Brighton & Hove City Council is strict on timelines. You must apply at least 7 full working days before your move. Bank Holidays do not count as working days."
        },
        {
          title: "What does it cost? (2025/2026 Prices)",
          content: "Standard Suspension: £51.00 per bay, per day. Admin Fee: £38.00 per application (reduced to £19.00 if you are a resident). Example: Suspending 2 bays for 1 day as a resident would cost roughly £121."
        },
        {
          title: "Can HappyVan help?",
          content: "We can't apply for the permit on your behalf, but we can certainly advise you. When you book your move with us, send us a photo of your street. We'll tell you exactly how many bays to book and where!"
        }
      ]
    }
  },
  {
    id: 2,
    title: "How to Film Your House for a Moving Quote in 2 Minutes",
    excerpt: "We know that life is busy. Waiting around for a moving company to visit your home for a survey isn't always convenient. That's why we've introduced WhatsApp Video Quotes.",
    image: "https://customer-assets.emergentagent.com/job_98b7cebb-f063-4be0-8c8c-dc4674976523/artifacts/7ykvip1c_brighton-996723_1280.jpg",
    video: "https://customer-assets.emergentagent.com/job_98b7cebb-f063-4be0-8c8c-dc4674976523/artifacts/716uzgpz_2-Minute_Video_Moving_Quote.mp4",
    date: "January 2025",
    readTime: "2 min watch",
    type: "video",
    content: {
      intro: "We know that life is busy. Waiting around for a moving company to visit your home for a survey isn't always convenient. That's why we've introduced WhatsApp Video Quotes—a faster, smarter way to get an accurate fixed-price quote without the wait.",
      solution: "However, a quote is only as accurate as the video you send! To help us give you the best price (and avoid any surprises on moving day), here is a quick guide on how to film the perfect 2-minute walkthrough.",
      sections: [
        {
          title: "1. Start Outside (The Access)",
          content: "Before you head indoors, show us where we will be parking. Is there a driveway? Are there parking restrictions or yellow lines? Is the path to the front door steep or narrow? Why this matters: If we need a parking suspension or a smaller van to navigate a tight lane, we need to know in advance!"
        },
        {
          title: "2. The Walkthrough (Room by Room)",
          content: "Walk through the house as if you were showing a guest around. Pan the camera slowly across each room. Open the cupboards: If you want us to move the contents of a wardrobe or cupboard, open the doors so we can see the volume of stuff inside. Don't forget the hidden spots: The loft, the cupboard under the stairs, and the garden shed are the most common places people forget to film."
        },
        {
          title: "3. Zoom in on the \"Problem Items\"",
          content: "Do you have a large American-style fridge freezer, a piano, or a large mirror? Zoom in on these. We may need to bring specialist covers or tools to dismantle them."
        },
        {
          title: "4. Send it to Us!",
          content: "You don't need to edit the video or worry about the lighting being perfect. Just head to our homepage, click the WhatsApp button, and attach your clips. We'll usually get back to you with a quote within hours."
        }
      ]
    }
  },
  {
    id: 3,
    title: "Moving with Pets: Tips to Keep Your Dog Calm on Moving Day",
    excerpt: "Moving house is cited as one of the most stressful life events for humans—but it can be just as confusing for our four-legged friends.",
    image: "https://customer-assets.emergentagent.com/job_98b7cebb-f063-4be0-8c8c-dc4674976523/artifacts/oyed2mg5_dachshund-672751_1280.jpg",
    date: "January 2025",
    readTime: "4 min read",
    type: "article",
    content: {
      intro: "Moving house is cited as one of the most stressful life events for humans—but it can be just as confusing for our four-legged friends.",
      solution: "As the owner of two mini wirehaired dachshunds, Archie and Lulu, I know exactly how much our pets rely on routine. When boxes start appearing and furniture starts moving, that routine goes out the window. Here are a few tips to keep your pets safe and happy during your move.",
      sections: [
        {
          title: "1. Create a 'Safe Room'",
          content: "On the day of the move, the front door will be wide open while we load the van. To prevent any escape artists from dashing out, keep your pets in one quiet room with the door shut. Put their bed, water, and favourite toys in there. (We are happy to work around this room until the very last moment!)"
        },
        {
          title: "2. Stick to the Routine",
          content: "Try to feed and walk them at the same times as usual. If you are moving locally in Sussex, taking them for a walk in a familiar park before the drive to the new house can help settle their nerves."
        },
        {
          title: "3. Pack their 'Essentials Box' Last",
          content: "Just like you need a kettle and mugs easily accessible, your dog needs their lead, food bowl, and bed ready the moment you arrive. Pack these items last so they are the first things off the van."
        },
        {
          title: "4. Let Us Know",
          content: "We love dogs. If we know you have pets in the house, our team will take extra care to ensure gates are kept closed and doors aren't left jarring open unnecessarily."
        }
      ],
      outro: "Moving is a big adventure for everyone in the family. With a little planning, we can make sure Archie, Lulu, and your own pets enjoy the ride."
    }
  },
  {
    id: 4,
    title: "How Much Does a 2-Bed House Move Cost in Brighton?",
    excerpt: "One of the most common questions we get asked. Here's a transparent breakdown of what to expect when moving a 2-bedroom house in Brighton & Hove.",
    image: "https://images.unsplash.com/photo-1618660920685-4505debb785a?fm=jpg&q=70&w=1280&h=720&fit=crop&auto=format",
    date: "May 2025",
    readTime: "3 min read",
    type: "article",
    content: {
      intro: "One of the most common questions we get asked is: 'How much will my move cost?' The honest answer is that it depends on several factors — but we believe in transparency, so here's a realistic breakdown.",
      solution: "For a typical 2-bedroom flat or house in Brighton, here's what affects the price and what you can expect to pay.",
      sections: [
        {
          title: "The Key Factors",
          content: "Volume of belongings (a fully furnished 2-bed vs a minimalist setup), access at both properties (stairs, lifts, narrow hallways), distance between pickup and delivery, time of week (weekends cost more), and whether you need packing services."
        },
        {
          title: "Typical Price Range",
          content: "For a local 2-bed move within Brighton & Hove, expect to pay between £350–£600. This includes 2 experienced movers, a Luton van, and typically 4–6 hours of work. Moves involving 3rd floor walk-ups or lots of heavy furniture will be towards the higher end."
        },
        {
          title: "What's Included",
          content: "All our quotes include transit insurance, blankets and straps to protect furniture, and the crew's time for loading and unloading. We don't charge hidden fuel surcharges or stair fees — it's all factored into your quote upfront."
        },
        {
          title: "How to Get an Exact Quote",
          content: "The fastest way is to call us or send a WhatsApp message with a few photos of the bigger items. We'll give you a fixed price — no obligation, no surprises on moving day."
        }
      ]
    }
  },
  {
    id: 5,
    title: "Navigating Bulky Furniture on Brighton's Narrow Staircases",
    excerpt: "Brighton's Victorian terraces are beautiful — but those narrow, winding staircases weren't designed for modern sofas. Here's how we handle it.",
    image: "https://images.unsplash.com/photo-1500134697073-dc9b3b4409c5?fm=jpg&q=70&w=1280&h=720&fit=crop&auto=format",
    date: "May 2025",
    readTime: "3 min read",
    type: "article",
    content: {
      intro: "If you've ever tried to carry a king-size mattress up a Victorian terrace staircase in Brighton, you know the struggle. Tight turns, low ceilings, and narrow doorways are par for the course in this city.",
      solution: "We move furniture in Brighton every week, so we've seen every tricky staircase and tight corner there is. Here's how we handle it.",
      sections: [
        {
          title: "Measure Before Moving Day",
          content: "We always ask for photos or video of stairwells and doorways. A quick measurement can tell us whether a sofa will make it up in one piece or needs the legs removed. It saves time and avoids the dreaded 'it won't fit' moment."
        },
        {
          title: "Disassembly Is Key",
          content: "Bed frames, wardrobes, and dining tables can almost always be taken apart. We carry the right tools and blankets to disassemble, protect, and reassemble at the other end. It's faster and safer than wrestling a fully assembled wardrobe around a 90-degree bend."
        },
        {
          title: "The Brighton Sofa Challenge",
          content: "Corner sofas are the number one problem item. If yours won't fit through the door, we can often remove hinges or take it through a window. In extreme cases, we've used ropes to hoist furniture to first-floor balconies — safely, of course."
        },
        {
          title: "Our Advice",
          content: "When booking your move, always mention if you're in a basement flat, above a shop, or in a building with no lift. We'll plan accordingly and make sure we bring the right team and equipment."
        }
      ]
    }
  },
  {
    id: 6,
    title: "What Size Van Do I Need for My Move?",
    excerpt: "Transit van or Luton? Here's a simple guide to help you figure out what size vehicle you'll need based on what you're moving.",
    image: "https://images.unsplash.com/photo-1710749093416-1e9cdde8d080?fm=jpg&q=70&w=1280&h=720&fit=crop&auto=format",
    date: "May 2025",
    readTime: "2 min read",
    type: "article",
    content: {
      intro: "Choosing the right van size can save you money and avoid the need for multiple trips. Here's a quick guide based on the moves we do every day.",
      solution: "We operate three van sizes — here's how to figure out which one you need.",
      sections: [
        {
          title: "Small Van (SWB Transit) — 6m\u00b3",
          content: "Perfect for: Studio flat, single room, or a few large items like a sofa and TV unit. Typical jobs: Student moves, eBay/Gumtree pickups, single furniture deliveries."
        },
        {
          title: "Medium Van (LWB Transit) — 12m\u00b3",
          content: "Perfect for: 1-bedroom flat with light furniture. Typical jobs: Flat shares, small office moves, multiple item deliveries."
        },
        {
          title: "Luton Van — 20m\u00b3",
          content: "Perfect for: 2–3 bedroom house, fully furnished. This is our most popular van for house moves. It fits a full household including white goods, beds, wardrobes, and boxes."
        },
        {
          title: "Not Sure? Send Us a Video",
          content: "If you're unsure, don't guess — give us a call or send a WhatsApp message describing your place. We'll tell you exactly what van size you need and give you an accurate quote."
        }
      ]
    }
  }
];

const Blog = () => {
  const [selectedPost, setSelectedPost] = useState(null);

  return (
    <section id="blog" className="py-24 bg-white relative">
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-teal-600 font-semibold text-sm uppercase tracking-wider mb-3">Our Blog</span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Moving Tips & Guides</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Helpful advice to make your move as smooth as possible.
          </p>
        </div>

        {/* Blog Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.map((post) => (
            <article 
              key={post.id}
              className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100 hover:border-teal-100"
            >
              {/* Image */}
              <div className="relative h-56 overflow-hidden">
                <img 
                  src={post.image} 
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                {/* Video indicator */}
                {post.type === 'video' && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <Play className="w-8 h-8 text-teal-600 ml-1" fill="currentColor" />
                    </div>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Meta */}
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {post.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {post.readTime}
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors line-clamp-2">
                  {post.title}
                </h3>

                {/* Excerpt */}
                <p className="text-gray-600 mb-4 line-clamp-3">
                  {post.excerpt}
                </p>

                {/* Read More */}
                <button 
                  onClick={() => setSelectedPost(post)}
                  className="inline-flex items-center gap-2 text-teal-600 font-semibold group-hover:gap-3 transition-all"
                >
                  {post.type === 'video' ? 'Watch Video' : 'Read More'}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* Blog Post Modal */}
      {selectedPost && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPost(null)}
        >
          <div 
            className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header - Video or Image */}
            <div className="relative">
              {selectedPost.type === 'video' && selectedPost.video ? (
                <div className="relative bg-black">
                  <video 
                    controls 
                    autoPlay
                    className="w-full max-h-[400px] object-contain"
                    poster={selectedPost.image}
                  >
                    <source src={selectedPost.video} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                  <button 
                    onClick={() => setSelectedPost(null)}
                    className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-colors z-10"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <div className="relative h-64">
                  <img 
                    src={selectedPost.image} 
                    alt={selectedPost.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <button 
                    onClick={() => setSelectedPost(null)}
                    className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-colors"
                    aria-label="Close blog post"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="flex items-center gap-4 text-sm text-white/80 mb-2">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {selectedPost.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {selectedPost.readTime}
                      </span>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-white">
                      {selectedPost.title}
                    </h2>
                  </div>
                </div>
              )}
            </div>

            {/* Title for video posts (below video) */}
            {selectedPost.type === 'video' && (
              <div className="px-6 pt-6 md:px-8 md:pt-8">
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-2">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {selectedPost.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {selectedPost.readTime}
                  </span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  {selectedPost.title}
                </h2>
              </div>
            )}

            {/* Modal Content */}
            <div className={`p-6 md:p-8 overflow-y-auto max-h-[50vh] ${selectedPost.type === 'video' ? 'pt-0 md:pt-0' : ''}`}>
              <p className="text-gray-700 text-lg mb-4 leading-relaxed">
                {selectedPost.content.intro}
              </p>
              <p className="text-teal-600 font-semibold mb-6">
                {selectedPost.content.solution}
              </p>

              {selectedPost.content.sections.map((section, index) => (
                <div key={index} className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {section.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {section.content}
                  </p>
                </div>
              ))}

              {selectedPost.content.outro && (
                <p className="text-gray-700 text-lg mb-6 leading-relaxed italic">
                  {selectedPost.content.outro}
                </p>
              )}

              <div className="mt-8 pt-6 border-t">
                <p className="text-gray-700 font-medium mb-4">
                  Ready to get moving? Contact us and let's get you to your new home, hassle-free.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button 
                    asChild
                    className="bg-teal-600 hover:bg-teal-700 text-white"
                  >
                    <a href="/get-quote">Get a Free Quote</a>
                  </Button>
                  {selectedPost.type === 'video' && (
                    <Button 
                      asChild
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <a href="#video-quote">Send Video via WhatsApp</a>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Blog;
